import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { initAllStores } from "../../utils/cache";
import { sendRequest } from "../../utils/api";
import FullPageSpinner from "../../components/FullPageSpinner";
import { FaCheckCircle } from "react-icons/fa";

export default function TokenVerificationPage() {
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [searchParams] = useSearchParams();
  const { action } = useParams(); // "reset-password" | "invite"
  const navigate = useNavigate();

  const validActions = ["reset-password", "invite"];

  useEffect(() => {
    if (!action || !validActions.includes(action)) {
      navigate("/404", { replace: true });
      return;
    }

    const verifyToken = async () => {
      const token = searchParams.get("token");
      if (!token) {
        navigate(`/${action}/invalid`, { replace: true });
        return;
      }

      try {
        const cache = await initAllStores();
        const response = await sendRequest(
          "GET",
          `/auth/verify?token=${token}`,
          {},
          false
        );

        if (!response?.success) {
          navigate(`/${action}/invalid`, { replace: true });
          return;
        }

        const { data } = response;

        if (data?.token && action === "invite") {
          localStorage.setItem("authToken", token);
        }

        if (data?.token && data?.user && data?.hotel) {
          const { token: authToken, user, hotel } = data;

          await cache.upsertOne("data", { ...hotel, id: "hotel" });
          await cache.upsertOne("data", { ...user, id: "user" });

          localStorage.setItem("authToken", authToken);
          localStorage.setItem("isOnline", "true");
          localStorage.setItem(
            "user",
            JSON.stringify({
              id: user.userId,
              role: user.role,
              permissions: user.permissions,
            })
          );
        }

        setVerified(true);
        setTimeout(() => {
          switch (action) {
            case "reset-password":
              navigate("/dashboard", { replace: true });
              break;
            case "invite":
              navigate("/staffs/signup", { replace: true });
              break;
            default:
              navigate("/login", { replace: true });
          }
        }, 2000);
      } catch (err: any) {
        navigate(`/${action}/invalid`, { replace: true });
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [searchParams, action, navigate]);

  if (loading) return <FullPageSpinner show={true} />;

  if (verified)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-center animate-fadeIn">
        <div className="bg-white shadow-md rounded-xl p-8 max-w-md w-full transform transition-all">
          <span className="block text-green-500 text-6xl mx-auto mb-4 animate-bounce">
            {FaCheckCircle({})}
          </span>

          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Verification Successful
          </h2>
          <p className="text-gray-600 mb-4">
            Your token has been verified successfully.
          </p>
          <div className="flex items-center justify-center space-x-2 text-gray-500 text-sm">
            <span className="animate-spin border-2 border-t-transparent border-gray-400 rounded-full w-4 h-4"></span>
            <span>Redirecting...</span>
          </div>
        </div>
      </div>
    );

  return null;
}
